import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FirstLoginDialog({ user, onPasswordChanged, onLogout }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [changing, setChanging] = useState(false);

  // Check if user is using a temp academy email
  const isTempEmail = user?.email?.endsWith("@academy.temp");
  const isAcademyPlayer = user?.roles?.includes("academy_player");

  const validateEmail = (email) => {
    if (!email) return true; // Optional, so empty is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill all password fields");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newEmail && !validateEmail(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setChanging(true);
    try {
      // Change password
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: oldPassword,
          new_password: newPassword,
          new_email: newEmail || null // Optional email update
        },
        { withCredentials: true }
      );

      if (newEmail) {
        toast.success("Password changed and email updated! Use your new email to login next time.");
      } else {
        toast.success("Password changed successfully!");
      }
      onPasswordChanged();
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChanging(false);
    }
  };

  if (!user || !user.first_login) return null;

  // Escaping the gate = logging out (returns to the login screen). We never
  // silently dismiss because first_login stays true, so the gate would just
  // re-open. Logging out is the safe, unambiguous exit.
  const handleExit = async () => {
    if (onLogout) {
      await onLogout();
    } else {
      window.location.href = "/";
    }
  };

  // For a returning user who doesn't know their temporary password: log them
  // out and send them straight to the self-service password-reset page.
  const handleForgotPassword = async () => {
    try {
      if (onLogout) await onLogout();
    } finally {
      const email = user?.email && !user.email.endsWith("@academy.temp") ? user.email : "";
      window.location.href = `/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`;
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) handleExit(); }}>
      <DialogContent data-testid="first-login-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-yellow-500" />
            Welcome! Set Up Your Account
          </DialogTitle>
          <DialogDescription>
            Change your temporary password to secure your account, or use the options below if you don't know it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
            <p className="text-sm text-yellow-400">
              <strong>Welcome to SCC Football Academy!</strong><br/>
              Please change your default password to secure your account.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Current Password</label>
            <Input
              type="password"
              placeholder="Enter your temporary password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              data-testid="old-password-input"
            />
            <p className="text-xs text-gray-500 mt-1">This is the password you received (e.g., Academy17C0001!)</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">New Password</label>
            <Input
              type="password"
              placeholder="Enter new password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Confirm New Password</label>
            <Input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="confirm-password-input"
            />
          </div>

          {/* Show email field for academy players with temp email */}
          {isAcademyPlayer && isTempEmail && (
            <>
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <label className="text-sm font-medium">Your Email Address (Optional)</label>
                </div>
                <Input
                  type="email"
                  placeholder="Enter your real email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="new-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add your email to enable password reset and receive notifications.
                  You can skip this and add it later in your profile.
                </p>
              </div>
            </>
          )}

          <Button
            onClick={handleChangePassword}
            disabled={changing}
            className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold"
            data-testid="change-password-button"
          >
            {changing ? "Saving..." : "Save & Continue"}
          </Button>

          {/* Escape hatches: reset password if the temp password is unknown,
              or simply log out to return to the login screen. */}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
              data-testid="first-login-forgot-password"
            >
              I don't know my password — reset it
            </button>
            <button
              type="button"
              onClick={handleExit}
              className="text-xs text-gray-400 hover:text-gray-300 underline underline-offset-2"
              data-testid="first-login-logout"
            >
              Log out
            </button>
          </div>

          {isAcademyPlayer && isTempEmail && !newEmail && (
            <p className="text-xs text-center text-gray-400">
              Current login: {user.email}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
